/**
 * Map viewport helpers — cull markers outside the visible area.
 */

export type LatLng = [number, number];

export type MapBounds = {
  ne: LatLng; // [lat, lng]
  sw: LatLng;
};

export type SettledMapView = {
  center: LatLng;
  zoom: number;
  bounds: MapBounds | null;
};

/** Expand bounds by a fraction so edge pins aren't clipped during small pans. */
export function padBounds(bounds: MapBounds, pad = 0.2): MapBounds {
  const latSpan = bounds.ne[0] - bounds.sw[0];
  const lngSpan = bounds.ne[1] - bounds.sw[1];
  // Handle antimeridian awkwardly: if span is huge, skip pad
  if (!Number.isFinite(latSpan) || !Number.isFinite(lngSpan) || lngSpan > 300) {
    return bounds;
  }
  const dLat = latSpan * pad;
  const dLng = lngSpan * pad;
  return {
    ne: [bounds.ne[0] + dLat, bounds.ne[1] + dLng],
    sw: [bounds.sw[0] - dLat, bounds.sw[1] - dLng],
  };
}

/**
 * Approximate visible lat/lng box from center + zoom (when pigeon hasn't
 * emitted bounds yet after a programmatic camera jump).
 */
export function estimateBoundsFromCenterZoom(
  center: LatLng,
  zoom: number,
  widthPx = 900,
  heightPx = 700
): MapBounds {
  const lat = center[0];
  const lng = center[1];
  const z = Number.isFinite(zoom) ? zoom : 10;
  const latRad = (lat * Math.PI) / 180;
  const cos = Math.max(0.05, Math.cos(latRad));
  // Web Mercator meters/pixel at equator ≈ 156543.03 / 2^z
  const metersPerPx = 156543.03392 * cos / Math.pow(2, z);
  const halfH = (heightPx / 2) * metersPerPx;
  const halfW = (widthPx / 2) * metersPerPx;
  const dLat = halfH / 111320;
  const dLng = halfW / (111320 * cos);
  return {
    ne: [lat + dLat, lng + dLng],
    sw: [lat - dLat, lng - dLng],
  };
}

export function pointInBounds(
  lat: number,
  lng: number,
  bounds: MapBounds | null | undefined
): boolean {
  if (!bounds) return true; // no bounds yet → keep markers until first settle
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  const { ne, sw } = bounds;
  if (lat > ne[0] || lat < sw[0]) return false;
  // Normal case (no dateline wrap)
  if (sw[1] <= ne[1]) {
    return lng >= sw[1] && lng <= ne[1];
  }
  // Dateline wrap: visible across ±180
  return lng >= sw[1] || lng <= ne[1];
}

export function anchorInBounds(
  anchor: LatLng | undefined | null,
  bounds: MapBounds | null | undefined
): boolean {
  if (!anchor) return false;
  return pointInBounds(anchor[0], anchor[1], bounds);
}

/** True if a GeoJSON feature's lat/lng properties or bbox touch the viewport. */
export function featureTouchesBounds(
  feature: {
    properties?: { lat?: number; lng?: number; slug?: string };
    bbox?: number[];
    geometry?: any;
  },
  bounds: MapBounds | null | undefined,
  alwaysIncludeSlugs?: Set<string> | string[]
): boolean {
  const slug = feature?.properties?.slug;
  if (slug && alwaysIncludeSlugs) {
    const set =
      alwaysIncludeSlugs instanceof Set
        ? alwaysIncludeSlugs
        : new Set(alwaysIncludeSlugs);
    if (set.has(slug)) return true;
  }
  if (!bounds) return true;
  const lat = Number(feature?.properties?.lat);
  const lng = Number(feature?.properties?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return pointInBounds(lat, lng, padBounds(bounds, 0.35));
  }
  // Fallback: sample first ring vertex
  const coords = feature?.geometry?.coordinates;
  const sample = firstCoord(coords);
  if (!sample) return true;
  return pointInBounds(sample[1], sample[0], padBounds(bounds, 0.35));
}

function firstCoord(coords: any): [number, number] | null {
  if (!coords) return null;
  if (typeof coords[0] === "number") return [coords[0], coords[1]];
  for (const c of coords) {
    const f = firstCoord(c);
    if (f) return f;
  }
  return null;
}
